import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  InputSignal,
  OnInit,
  signal,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { GsFormFieldBasic } from '../interfaces/gs-form-schema';

@Component({
  imports: [ReactiveFormsModule, TranslatePipe],
  template: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: GsBaseFormInputComponent
    }
  ]
})
export class GsBaseFormInputComponent<ValueType, GsFormFieldType extends GsFormFieldBasic<ValueType>>
  implements ControlValueAccessor, OnInit
{
  // Services
  protected readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  // Inputs
  /** The schema where the options for this component come from */
  schema: InputSignal<GsFormFieldType> = input.required();
  /** The form-control to use for the data */
  control: InputSignal<FormControl<ValueType | undefined | null>> = input(new FormControl());

  // Properties & Signals
  /** The form-control to use for the data */
  internalControl: WritableSignal<FormControl<ValueType | undefined | null>> = signal<FormControl<ValueType | undefined | null>>(
    new FormControl(undefined)
  );

  /** Callback registered by the forms API, invoked on every value change */
  private onChangeCallback: (val: ValueType | undefined | null) => void = () => undefined;
  /** Callback registered by the forms API, invoked once the field has been interacted with */
  private onTouchedCallback: () => void = () => undefined;

  // Methods
  ngOnInit(): void {
    this.initField();
  }

  /**
   * Initializes the field based on the schema and control inputs
   * @throws Error if no schema is provided
   */
  protected initField(): void {
    if (!this.schema()) {
      throw new Error('No schema provided');
    }

    // `writeValue` never emits, so a change here always originates from the user
    this.internalControl()
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        this.onChangeCallback(val);
        this.onTouchedCallback();
        if (this.control() && !this.isEquivalent(this.control().value, val)) {
          this.control().setValue(val);
        }
      });

    if (this.control()) {
      this.writeValue(this.control().value);
      // gs-form passes the control as input instead of binding it via formControlName, so setDisabledState is
      // never called: take over the disabled state of a readonly field here.
      if (this.control().disabled) this.internalControl().disable({ emitEvent: false });
      this.control()
        .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((val) => {
          if (!this.isEquivalent(this.internalControl().value, val)) {
            this.writeValue(val);
          }
        });
    }
  }

  /**
   * Compares two values, treating null and undefined as equivalent.
   * Uses strict equality for all other comparisons to avoid JS coercion pitfalls.
   */
  private isEquivalent(a: ValueType | undefined | null, b: ValueType | undefined | null): boolean {
    if (a == null && b == null) return true;
    return a === b;
  }

  /**
   * Writes a value from outside into the field. The template reads the FormControl, which is no signal,
   * so the view is marked for check.
   * @param val - The value to write.
   */
  writeValue(val: ValueType | undefined | null): void {
    this.internalControl().setValue(val ?? null, { emitEvent: false });
    this.changeDetectorRef.markForCheck();
  }
  registerOnChange(fn: (val: ValueType | undefined | null) => void): void {
    this.onChangeCallback = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }
  /**
   * Enables or disables the field and marks the view for check, as the template reads the disabled state of the FormControl.
   * @param isDisabled - Whether the field is disabled.
   */
  setDisabledState(isDisabled: boolean): void {
    this.internalControl()[isDisabled ? 'disable' : 'enable']();
    this.changeDetectorRef.markForCheck();
  }
}
